// frontend/src/components/Charts.js
// Thin, reusable ECharts wrapper components using Eureka theme
import { useContext, useRef, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { echartsTheme, echartsThemeDark, COLORS, PALETTE, fmtNum } from '../utils/theme';
import { ThemeContext } from '../context/ThemeContext';

function useTheme() {
  const { dark } = useContext(ThemeContext);
  return dark ? echartsThemeDark : echartsTheme;
}

// ── Bar Chart ─────────────────────────────────────────────────────────────
export function BarChart({ categories, series, height = 240, horizontal = false, onClick, xFormatter, tooltipFormatter, markLines = [] }) {
  const theme = useTheme();

  // Smart default formatter: show 2 decimal places for values < 1, otherwise fmtNum
  const defaultXFormatter = (v) => {
    if (v == null) return '';
    if (Math.abs(v) < 1 && v !== 0) return v.toFixed(2);
    return fmtNum(v);
  };

  const xFmt = xFormatter || defaultXFormatter;

  const option = {
    ...theme,
    grid: {
      ...theme.grid,
      bottom: horizontal ? 28 : 40,
      left:   horizontal ? 16 : 48,
      right:  horizontal ? 24 : 16,
      containLabel: horizontal,          // lets ECharts auto-size for y-axis labels
    },
    tooltip: { ...theme.tooltip, trigger: 'axis', ...(tooltipFormatter ? { valueFormatter: tooltipFormatter } : {}) },
    legend: series.length > 1 ? { ...theme.legend, bottom: 0 } : { show: false },
    xAxis: horizontal
      ? { ...theme.yAxis, type: 'value', axisLabel: { ...theme.yAxis.axisLabel, formatter: xFmt, fontSize: 10 } }
      : { ...theme.xAxis, type: 'category', data: categories,
          axisLabel: { ...theme.xAxis.axisLabel, rotate: categories.length > 8 ? 35 : 0, interval: 0 } },
    yAxis: horizontal
      ? { ...theme.xAxis, type: 'category', data: categories, inverse: true,
          axisLabel: { ...theme.xAxis.axisLabel, fontSize: 11, overflow: 'truncate', width: 100 } }
      : { ...theme.yAxis, type: 'value', axisLabel: { ...theme.yAxis.axisLabel, formatter: fmtNum } },
    series: series.map((s, i) => ({
      name: s.name,
      type: 'bar',
      data: s.data,
      barMaxWidth: 32,
      itemStyle: { color: s.color || PALETTE[i], borderRadius: horizontal ? [0,3,3,0] : [3,3,0,0] },
      emphasis: { itemStyle: { opacity: 0.85 } },
      ...(i === 0 && markLines.length ? {
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: '#9CA3AF', type: 'dashed', width: 1.5 },
          label: { show: false },
          data: markLines.map(v => (horizontal ? { xAxis: v } : { yAxis: v })),
        },
      } : {}),
    })),
  };

  return (
    <ReactECharts
      option={option}
      style={{ height }}
      onEvents={onClick ? { click: onClick } : {}}
    />
  );
}

// ── Line Chart ────────────────────────────────────────────────────────────
// pointLabels: true → show value labels on every point; pass a function to
// format them (e.g. v => `${v}%`)
export function LineChart({ categories, series, height = 240, smooth = true, pointLabels = false, wrapLegend = false, legendRows = 2, tooltipOrder }) {
  const theme = useTheme();
  const labelFmt = typeof pointLabels === 'function' ? pointLabels : (v => v);

  // wrapLegend → reserve enough space below the x-axis for the (wrapped) legend so
  // it never collides with the axis labels. legendRows is estimated by the caller
  // from the series count; ~22px per legend row + 24px for the axis labels.
  const wrapBottom = 24 + legendRows * 22;

  const option = {
    ...theme,
    grid: { ...theme.grid, bottom: wrapLegend ? wrapBottom : (series.length > 2 ? 56 : (series.length > 1 ? 48 : 28)) },
    // confine keeps a tall many-series tooltip inside the chart box so its top
    // (highest values, first under valueDesc) never scrolls off the top of the screen
    tooltip: {
      ...theme.tooltip,
      trigger: 'axis',
      ...(tooltipOrder ? { order: tooltipOrder } : {}),
      ...(wrapLegend ? { confine: true, extraCssText: 'max-height:70vh;overflow:auto;' } : {}),
    },
    // Many series (e.g. 15 postcode names) → single scrollable legend row so it
    // never wraps into the x-axis labels — unless wrapLegend, which wraps to show all.
    legend: series.length > 1
      ? { ...theme.legend, bottom: 0,
          ...(wrapLegend
                ? { type: 'plain', width: '96%', left: 'center' }
                : (series.length > 2 ? { type: 'scroll' } : {})) }
      : { show: false },
    xAxis: { ...theme.xAxis, type: 'category', data: categories },
    yAxis: { ...theme.yAxis, type: 'value', axisLabel: { ...theme.yAxis.axisLabel, formatter: fmtNum } },
    series: series.map((s, i) => ({
      name: s.name,
      type: 'line',
      data: s.data,
      smooth,
      showSymbol: !!pointLabels,
      symbolSize: pointLabels ? 7 : 4,
      label: pointLabels
        ? { show: true, fontSize: 9.5, fontWeight: 600, color: s.color || PALETTE[i],
            formatter: p => (p.value != null ? labelFmt(p.value) : '') }
        : undefined,
      lineStyle: { width: 2, color: s.color || PALETTE[i] },
      itemStyle: { color: s.color || PALETTE[i] },
      areaStyle: s.area ? { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
          { offset: 0, color: (s.color || PALETTE[i]) + '28' },
          { offset: 1, color: (s.color || PALETTE[i]) + '00' },
        ]}} : undefined,
    })),
  };

  return <ReactECharts option={option} style={{ height }} />;
}

// ── Donut Chart ───────────────────────────────────────────────────────────
export function DonutChart({ data, height = 240, onClick }) {
  const theme = useTheme();

  const option = {
    ...theme,
    tooltip: { ...theme.tooltip, trigger: 'item',
      formatter: (p) => `${p.marker}${p.name}<br/><b>${fmtNum(p.value)}</b> (${p.percent.toFixed(1)}%)` },
    legend: { ...theme.legend, orient: 'vertical', right: 8, top: 'middle' },
    series: [{
      type: 'pie',
      radius: ['48%', '72%'],
      center: ['38%', '50%'],
      padAngle: 2,
      data: data.map((d, i) => ({ ...d, itemStyle: { color: d.color || PALETTE[i] } })),
      label: { show: false },
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.15)' } },
    }],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height }}
      onEvents={onClick ? { click: onClick } : {}}
    />
  );
}

// ── Pie Chart (labelled with % on each slice) ──────────────────────────────
export function PieChart({ data, height = 320, onClick }) {
  const theme = useTheme();

  const option = {
    ...theme,
    tooltip: { ...theme.tooltip, trigger: 'item',
      formatter: (p) => `${p.marker}${p.name}<br/><b>${p.percent.toFixed(1)}%</b>` },
    legend: { ...theme.legend, type: 'plain', orient: 'horizontal', bottom: 0, left: 'center' },
    series: [{
      type: 'pie',
      radius: ['28%', '56%'],
      center: ['50%', '40%'],
      padAngle: 1,
      minShowLabelAngle: 6,            // hide labels on tiny slivers (tooltip still works)
      data: data.map((d, i) => ({ ...d, itemStyle: { color: d.color || PALETTE[i] } })),
      label: { show: true, formatter: '{b}: {d}%', fontSize: 10 },
      labelLine: { show: true, length: 8, length2: 8 },
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.15)' } },
    }],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height }}
      onEvents={onClick ? { click: onClick } : {}}
    />
  );
}

// ── Stacked Bar ───────────────────────────────────────────────────────────
// normalize=true → 100% stacked (each bar sums to 100%, values shown as %)
export function StackedBar({ categories, series, height = 240, horizontal = false, normalize = false }) {
  const theme = useTheme();

  // Pre-compute percentages when normalize is true
  const displaySeries = normalize
    ? (() => {
        const totals = categories.map((_, i) =>
          series.reduce((sum, s) => sum + (s.data[i] || 0), 0)
        );
        return series.map(s => ({
          ...s,
          data: s.data.map((v, i) => totals[i] ? +((v / totals[i]) * 100).toFixed(1) : 0),
        }));
      })()
    : series;

  const pctFmt = v => `${Number(v).toFixed(1)}%`;

  const option = {
    ...theme,
    grid: { ...theme.grid, bottom: horizontal ? 36 : 64, left: 16, right: 16, containLabel: true },
    tooltip: {
      ...theme.tooltip,
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      ...(normalize && {
        formatter: params => {
          const header = `<b>${params[0].axisValue}</b>`;
          const rows = params
            .filter(p => p.value > 0)
            .map(p => `${p.marker}${p.seriesName}: <b>${p.value}%</b>`)
            .join('<br/>');
          return `${header}<br/>${rows}`;
        },
      }),
    },
    legend: { ...theme.legend, bottom: 0, type: 'scroll' },
    xAxis: horizontal
      ? { ...theme.yAxis, type: 'value', max: normalize ? 100 : undefined,
          axisLabel: { formatter: normalize ? pctFmt : fmtNum, fontSize: 10 } }
      : { ...theme.xAxis, type: 'category', data: categories,
          axisLabel: { ...theme.xAxis.axisLabel, rotate: categories.length > 6 ? 45 : 0, interval: 0 } },
    yAxis: horizontal
      ? { ...theme.xAxis, type: 'category', data: categories, inverse: true,
          axisLabel: { overflow: 'truncate', fontSize: 11 } }
      : { ...theme.yAxis, type: 'value', max: normalize ? 100 : undefined,
          axisLabel: { formatter: normalize ? pctFmt : fmtNum, fontSize: 10 } },
    series: displaySeries.map((s, i) => ({
      name: s.name,
      type: 'bar',
      stack: 'total',
      data: s.data,
      itemStyle: {
        color: s.color || PALETTE[i],
        borderRadius: i === displaySeries.length - 1 ? (horizontal ? [0,3,3,0] : [3,3,0,0]) : 0,
      },
      label: normalize
        ? { show: true, formatter: p => p.value >= 6 ? `${p.value}%` : '', fontSize: 9, color: '#fff' }
        : { show: false },
    })),
  };

  return <ReactECharts option={option} style={{ height }} />;
}

// ── Dual-Axis Bar ─────────────────────────────────────────────────────────
// series1 → bars on left y-axis (users)
// series2 → bars on right y-axis (sessions)
export function DualAxisBar({ categories, series1, series2, height = 280, onClick }) {
  const theme = useTheme();

  const option = {
    ...theme,
    grid: { ...theme.grid, bottom: 64, left: 64, right: 64, containLabel: true },
    tooltip: { ...theme.tooltip, trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { ...theme.legend, bottom: 0 },
    xAxis: {
      ...theme.xAxis,
      type: 'category',
      data: categories,
      axisLabel: { ...theme.xAxis.axisLabel, rotate: categories.length > 5 ? 45 : 0, interval: 0 },
    },
    yAxis: [
      {
        ...theme.yAxis,
        type: 'value',
        name: series1.name,
        nameTextStyle: { color: series1.color || PALETTE[0], fontSize: 10, padding: [0,0,0,40] },
        axisLabel: { ...theme.yAxis.axisLabel, formatter: fmtNum },
        axisLine: { show: true, lineStyle: { color: series1.color || PALETTE[0] } },
      },
      {
        ...theme.yAxis,
        type: 'value',
        name: series2.name,
        nameTextStyle: { color: series2.color || PALETTE[1], fontSize: 10, padding: [0,40,0,0] },
        axisLabel: { ...theme.yAxis.axisLabel, formatter: fmtNum },
        axisLine: { show: true, lineStyle: { color: series2.color || PALETTE[1] } },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: series1.name,
        type: 'bar',
        data: series1.data,
        yAxisIndex: 0,
        barMaxWidth: 48,
        itemStyle: { color: series1.color || PALETTE[0], borderRadius: [3,3,0,0] },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
      {
        name: series2.name,
        type: 'bar',
        data: series2.data,
        yAxisIndex: 1,
        barMaxWidth: 48,
        itemStyle: { color: series2.color || PALETTE[1], borderRadius: [3,3,0,0], opacity: 0.78 },
        emphasis: { itemStyle: { opacity: 1 } },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height }} onEvents={onClick ? { click: onClick } : {}} />;
}

// ── Dual-Axis Bar + Line ──────────────────────────────────────────────────
// series1 → bars on left y-axis (users)
// series2 → bars on right y-axis (sessions)
// line    → line on a hidden 0–100 axis (percentage metric)
export function DualAxisBarLine({ categories, series1, series2, line, height = 300 }) {
  const theme = useTheme();

  const option = {
    ...theme,
    grid: { ...theme.grid, bottom: 72, left: 64, right: 64 },
    tooltip: {
      ...theme.tooltip,
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: params => {
        const header = `<b>${params[0].axisValue}</b>`;
        const rows = params.map(p => {
          const val = p.value == null ? '—'
            : p.seriesName === line.name ? `${p.value}%` : fmtNum(p.value);
          return `${p.marker}${p.seriesName}: <b>${val}</b>`;
        }).join('<br/>');
        return `${header}<br/>${rows}`;
      },
    },
    legend: { ...theme.legend, bottom: 0 },
    xAxis: {
      ...theme.xAxis,
      type: 'category',
      data: categories,
      axisLabel: { ...theme.xAxis.axisLabel, rotate: categories.length > 8 ? 35 : 0, interval: 0 },
    },
    yAxis: [
      {
        ...theme.yAxis,
        type: 'value',
        name: series1.name,
        nameTextStyle: { color: series1.color || PALETTE[0], fontSize: 10, padding: [0,0,0,40] },
        axisLabel: { ...theme.yAxis.axisLabel, formatter: fmtNum },
        axisLine: { show: true, lineStyle: { color: series1.color || PALETTE[0] } },
      },
      {
        ...theme.yAxis,
        type: 'value',
        name: series2.name,
        nameTextStyle: { color: series2.color || PALETTE[1], fontSize: 10, padding: [0,40,0,0] },
        axisLabel: { ...theme.yAxis.axisLabel, formatter: fmtNum },
        axisLine: { show: true, lineStyle: { color: series2.color || PALETTE[1] } },
        splitLine: { show: false },
      },
      { type: 'value', min: 0, max: 100, show: false },
    ],
    series: [
      {
        name: series1.name,
        type: 'bar',
        data: series1.data,
        yAxisIndex: 0,
        barMaxWidth: 40,
        itemStyle: { color: series1.color || PALETTE[0], borderRadius: [3,3,0,0] },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
      {
        name: series2.name,
        type: 'bar',
        data: series2.data,
        yAxisIndex: 1,
        barMaxWidth: 40,
        itemStyle: { color: series2.color || PALETTE[1], borderRadius: [3,3,0,0], opacity: 0.78 },
        emphasis: { itemStyle: { opacity: 1 } },
      },
      {
        name: line.name,
        type: 'line',
        data: line.data,
        yAxisIndex: 2,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2.5, color: line.color || PALETTE[4] },
        itemStyle: { color: line.color || PALETTE[4] },
        label: { show: true, formatter: p => p.value != null ? `${p.value}%` : '', fontSize: 9, color: line.color || PALETTE[4] },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height }} />;
}

// ── Heatmap ───────────────────────────────────────────────────────────────
// apps  : array of app name strings (both axes)
// data  : [[app_y, app_x, overlap_pct], ...]
//   x-axis = app_y  (column: "also uses this app")
//   y-axis = app_x  (row:    "% of this app's users who …")
// emphasize : optional axis value(s) (e.g. 'All Apps') rendered bold on both
//             axes and with bold cell values in their row/column
// xApps/yApps : optional distinct axis lists (default: `apps` on both axes)
// redData   : optional second cell set rendered on a red colour ramp
//             (own hidden visualMap) — used to highlight risk-style metrics
export function HeatmapChart({ apps, data, height = 420, tooltipFormatter, emphasize = null, xApps = null, yApps = null, redData = null }) {
  const theme = useTheme();
  const chartRef = useRef(null);

  useEffect(() => () => {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (inst && !inst.isDisposed()) inst.dispose();
  }, []);

  const defaultTooltip = p =>
    `<b>${p.data[1]}</b> users who also use <b>${p.data[0]}</b><br/>` +
    `Overlap: <b>${p.data[2] != null ? p.data[2] + '%' : '—'}</b>`;

  const emph = Array.isArray(emphasize) ? emphasize : emphasize ? [emphasize] : [];
  const emphasizeLabel = emph.length
    ? {
        formatter: v => (emph.includes(v) ? `{b|${v}}` : v),
        rich: { b: { fontWeight: 700, fontSize: 10.5, color: '#111827' } },
      }
    : {};

  const option = {
    ...theme,
    grid: { top: 80, left: 16, right: 80, bottom: 16, containLabel: true },
    tooltip: {
      ...theme.tooltip,
      trigger: 'item',
      formatter: tooltipFormatter || defaultTooltip,
    },
    xAxis: {
      type: 'category',
      data: xApps || apps,
      position: 'top',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontSize: 10,
        color: '#6B7280',
        rotate: 40,
        interval: 0,
        ...emphasizeLabel,
      },
    },
    yAxis: {
      type: 'category',
      data: yApps || apps,
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { fontSize: 10, color: '#6B7280', ...emphasizeLabel },
    },
    visualMap: [
      {
        min: 0,
        max: 100,
        show: true,
        orient: 'vertical',
        right: 0,
        top: 'middle',
        text: ['100%', '0%'],
        textStyle: { fontSize: 10, color: '#9CA3AF' },
        inRange: { color: ['#E1F5EE', '#5DCAA5', '#1DB89A', '#0F6E56'] },
        seriesIndex: 0,
      },
      // Hidden red ramp for the optional risk series
      ...(redData ? [{
        min: 0,
        max: 100,
        show: false,
        inRange: { color: ['#FEF2F2', '#FCA5A5', '#EF4444', '#991B1B'] },
        seriesIndex: 1,
      }] : []),
    ],
    series: [
      {
        type: 'heatmap',
        data,
        label: {
          show: true,
          // data[3], when present, is an absolute figure displayed instead of the %
          // (the % slot then only drives the colour scale)
          formatter: p => {
            const v = p.data[3] != null
              ? fmtNum(p.data[3])
              : p.data[2] != null ? `${p.data[2]}%` : '—';
            return emph.includes(p.data[0]) || emph.includes(p.data[1]) ? `{b|${v}}` : v;
          },
          fontSize: 9.5,
          color: '#0F172A',
          fontWeight: 500,
          rich: { b: { fontWeight: 700, fontSize: 10, color: '#0F172A' } },
        },
        itemStyle: { borderWidth: 2, borderColor: '#fff' },
        emphasis: { itemStyle: { shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.2)' } },
      },
      ...(redData ? [{
        type: 'heatmap',
        data: redData,
        label: {
          show: true,
          formatter: p => (p.data[2] != null ? `{b|${p.data[2]}%}` : '—'),
          fontSize: 9.5,
          rich: { b: { fontWeight: 700, fontSize: 10, color: '#7F1D1D' } },
        },
        itemStyle: { borderWidth: 2, borderColor: '#fff' },
        emphasis: { itemStyle: { shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.2)' } },
      }] : []),
    ],
  };

  return <ReactECharts ref={chartRef} option={option} style={{ height }} notMerge />;
}

// ── Bubble / Scatter Chart ────────────────────────────────────────────────
// data: array of { x, y, size, label, dim, color? }
// x = universe (segment size), y = opportunity_pct, size = opportunity_users
// Quadrant reference lines drawn at medianX / medianY.
export function BubbleChart({ data = [], height = 420, xLabel = 'Universe', yLabel = 'Opportunity %' }) {
  const theme = useTheme();

  const medianX = data.length
    ? [...data].sort((a, b) => a.x - b.x)[Math.floor(data.length / 2)]?.x
    : null;
  const medianY = data.length
    ? [...data].sort((a, b) => a.y - b.y)[Math.floor(data.length / 2)]?.y
    : null;

  // Group by dim for separate series (enables per-dim colour + legend)
  const dims = [...new Set(data.map(d => d.dim))];
  const dimColors = { age: COLORS.teal, gender: COLORS.blue, geo: COLORS.indigo };

  const series = dims.map(dim => ({
    name: dim.charAt(0).toUpperCase() + dim.slice(1),
    type: 'scatter',
    symbolSize: d => Math.max(8, Math.sqrt(d[2]) * 0.06),
    itemStyle: { color: dimColors[dim] || PALETTE[dims.indexOf(dim)], opacity: 0.75 },
    data: data
      .filter(d => d.dim === dim)
      .map(d => [d.x, d.y, d.size, d.label, d.dim]),
    emphasis: { itemStyle: { opacity: 1 } },
  }));

  // Quadrant reference lines
  const markLines = medianX != null && medianY != null ? [{
    type: 'scatter', data: [],
    markLine: {
      silent: true,
      symbol: 'none',
      lineStyle: { type: 'dashed', color: '#D1D5DB', width: 1 },
      data: [
        [{ coord: [medianX, 0] }, { coord: [medianX, Math.max(...data.map(d => d.y)) * 1.1] }],
        [{ coord: [0, medianY] }, { coord: [Math.max(...data.map(d => d.x)) * 1.1, medianY] }],
      ],
    },
  }] : [];

  const option = {
    ...theme,
    legend: { data: dims.map(d => d.charAt(0).toUpperCase() + d.slice(1)), top: 0, right: 0 },
    grid: { top: 36, left: 16, right: 24, bottom: 48, containLabel: true },
    tooltip: {
      ...theme.tooltip,
      trigger: 'item',
      formatter: p => {
        const [x, y, size, label] = p.data;
        return `<b>${label}</b><br/>Universe: ${fmtNum(x)}<br/>Gap: ${y}%<br/>Opportunity: ${fmtNum(size)}`;
      },
    },
    xAxis: {
      type: 'value',
      name: xLabel,
      nameLocation: 'middle',
      nameGap: 28,
      axisLabel: { formatter: v => fmtNum(v) },
    },
    yAxis: {
      type: 'value',
      name: yLabel,
      nameLocation: 'end',
      nameGap: 6,
      nameTextStyle: { align: 'left', padding: [0, 0, 0, 4] },
      axisLabel: { formatter: v => `${v}%` },
    },
    series: [...series, ...markLines],
  };

  return <ReactECharts option={option} style={{ height }} />;
}

// ── Card wrapper ──────────────────────────────────────────────────────────
export function ChartCard({ title, sub, children, action, style }) {
  return (
    <div className="card" style={style}>
      <div className="card-header">
        <div>
          <div className="card-title">{title}</div>
          {sub && <div className="card-sub">{sub}</div>}
        </div>
        {action}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

// ── Loading / Error ───────────────────────────────────────────────────────
export function ChartLoader({ height = 220 }) {
  return (
    <div className="loading-state" style={{ height }}>
      <div className="spinner" />
      <span>Querying BigQuery…</span>
    </div>
  );
}

export function ChartError({ message, height = 220 }) {
  return (
    <div className="loading-state" style={{ height, color: COLORS.negative }}>
      ⚠ {message}
    </div>
  );
}

// ── Picker callout banner + section wrapper ───────────────────────────────
// Usage:
//   <PickerSection callout={{ title, subtitle, highlights: [{ label, desc }] }} style={...}>
//     <div className="card-header">...</div>
//     ...
//   </PickerSection>
// When callout is null/false the component renders a plain .card.

function PickerCalloutBanner({ title, subtitle, highlights = [] }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(29,184,154,0.14) 0%, rgba(29,184,154,0.28) 100%)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
      padding: '18px 22px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 15, marginBottom: 3 }}>{title}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{subtitle}</div>
        </div>
        <span style={{
          background: 'linear-gradient(145deg, rgba(29,184,154,0.90) 0%, rgba(14,154,126,0.80) 100%)',
          color: '#fff',
          fontSize: 10,
          fontWeight: 700,
          padding: '4px 11px',
          borderRadius: 20,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          border: '1px solid rgba(255,255,255,0.30)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 3px 10px rgba(29,184,154,0.30)',
          whiteSpace: 'nowrap',
        }}>
          Preview
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${highlights.length}, 1fr)`, gap: 10 }}>
        {highlights.map(h => (
          <div key={h.label} style={{
            background: 'rgba(29,184,154,0.09)',
            border: '1px solid rgba(29,184,154,0.28)',
            borderRadius: 10,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.30)',
            padding: '10px 14px',
          }}>
            <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 12, marginBottom: 5 }}>{h.label}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.55 }}>{h.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PickerSection({ callout, style, children }) {
  if (!callout) {
    return <div className="card" style={style}>{children}</div>;
  }
  return (
    <div style={{
      borderRadius: 16,
      overflow: 'hidden',
      border: '2px solid rgba(29,184,154,0.40)',
      boxShadow: '0 6px 32px rgba(29,184,154,0.16), 0 1px 2px rgba(29,120,102,0.08)',
      ...style,
    }}>
      <PickerCalloutBanner
        title={callout.title}
        subtitle={callout.subtitle}
        highlights={callout.highlights}
      />
      <div className="card" style={{ margin: 0, borderRadius: 0, border: 'none', boxShadow: 'none' }}>
        {children}
      </div>
    </div>
  );
}
